import { createContext, useContext } from "react";
import { makeAutoObservable } from "mobx";
import { LanguageEnum } from "../models/enums/language.enum";
import { StatusEnum } from "../models/enums/status.enum";
import {ContextDTO} from "../models/dto/context-dto.ts";
import type {PairingInformations} from "../models/pairing-informations.ts";
import type {Report} from "../models/report.ts";
import {InitialiseApplicationResult} from "../routes/initialise-application.ts";
import {TvDisplayModeEnum} from "../models/enums/tv-display-mode.enum.ts";

export class AppStore {
    context: Partial<ContextDTO> = {};
    locale: LanguageEnum = LanguageEnum.EN_US;
    report: Partial<Report> = {};
    messages: Record<string, string> = {};
    pairingInformation: Partial<PairingInformations> = {};
    status: StatusEnum = StatusEnum.UNPAIRED;
    isTranslationsLoading = false;
    hasAppointment?: boolean;

    constructor() {
        makeAutoObservable(this, {}, { autoBind: true });
    }

    // actions
    setContext(context: Partial<ContextDTO>) { this.context = {...this.context, ...context}; }
    setLocale(locale: LanguageEnum) { this.locale = locale; }
    setReport(report: Partial<Report>) { this.report = report; }
    setMessages(messages: Record<string, string>) { this.messages = messages; }
    setPairingInformation(pairingInformation: PairingInformations) { this.pairingInformation = pairingInformation; }
    setStatus(status: StatusEnum) { this.status = status; }
    setIsTranslationsLoading(isTranslationsLoading: boolean) { this.isTranslationsLoading = isTranslationsLoading; }
    setHasAppointment(hasAppointment?: boolean) { this.hasAppointment = hasAppointment; }
    setInitState = ({ context, messages, status: initialStatus, pairingInformation }: InitialiseApplicationResult) => {
        this.setContext(context);
        this.setMessages(messages);
        this.setStatus(initialStatus);
        if (pairingInformation) {
            this.setPairingInformation(pairingInformation);
        }
    }

    // handy computed values
    get isPaired() { return this.status !== StatusEnum.UNPAIRED; }

    get welcomeMediaPath(): string | undefined {
        if (this.context.displayMode !== TvDisplayModeEnum.WELCOME) return undefined;
        const media = this.hasAppointment ? this.context.withAppointmentMedia : this.context.withoutAppointmentMedia;
        return media?.path;
    }
}

export const appStore = new AppStore();

/** Provide the store through React context (simple and tree-shakable) */
export const AppStoreContext = createContext<AppStore>(appStore);
export const useAppStore = () => useContext(AppStoreContext);
